/**
 * @file index.tsx
 *
 * @fileoverview Job detail page, can be accessed by going to /job/{job-name}.
 * The page displays the full job detail page.
 */

import React, { useContext, useEffect } from 'react';
import { User } from '~/jobiqo/models/user';
import { Layout } from '../Layout';
import { Job } from '~/jobiqo/types/types';
import { OrganizationBannerWidget } from '~/jobiqo/components/Organization/Widgets/Banner';
import JobDetailPanel from '~/jobiqo/components/Job/Detail';
import OrganizationGalleryWidget from '~/jobiqo/components/Organization/Widgets/Gallery';
import OrganizationVideoGalleryWidget from '~/jobiqo/components/Organization/Widgets/VideoGallery';
import {
  JobAlertContext,
  JobAlertContextInterface,
} from '~/jobiqo/components/JobAlert/context';
import JobAlertModal from '~/jobiqo/components/JobAlert/Modal';
import JobRecommenderJobs2Job from '~/jobiqo/components/JobRecommender/components/Jobs2Job';
import JobRecommenderSimilarJobs from '~/jobiqo/components/JobRecommender/components/SimilarJobs';
import { getIsJobPublished } from '~/jobiqo/components/Job/Detail/utils';
import { Main } from '~/vcl/atoms/06-layout/09-main';
import { Box } from '~/vcl/atoms/07-box';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { ScrollToElement } from '~/vcl/atoms/06-layout/11-scroll-to-element';
import JobsByOrganizationProfileId from '~/jobiqo/components/Organization/JobsByOrganizationProfileId';
import OrganizationAboutWidget from '~/jobiqo/components/Organization/Widgets/About';
import { useConfig } from '~/jobiqo/context/config';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';
import ApplicantRecommenderApplicants2Job from '~/jobiqo/components/ApplicantRecommender/components/Applicants2Job';
import { isRecruiter } from '~/jobiqo/lib/auth';

interface JobPageDetailProps {
  /**
   * The job entity.
   */
  job: Job;
  /**
   * The current user.
   */
  user: User;
  /**
   * If external anchors in the main job description should get rel=nofollow.
   */
  noFollowExternalLinks?: boolean;
  /**
   * If external anchors in the main job description should be rendered as plain text.
   */
  plainTextExternalLinks?: boolean;
}

export const JobPageDetail = ({
  job,
  user,
  noFollowExternalLinks = false,
  plainTextExternalLinks = false,
}: JobPageDetailProps) => {
  const { setJobId }: JobAlertContextInterface = useContext(JobAlertContext);
  const { JOBS } = useFrontendConfig();
  const SHOW_ORGANIZATION_JOBS_ON_DETAIL_PAGE =
    JOBS.SHOW_ORGANIZATION_JOBS_ON_DETAIL_PAGE;
  const SHOW_ORGANIZATION_PRESENTATION_ON_DETAIL_PAGE =
    JOBS.SHOW_ORGANIZATION_PRESENTATION_ON_DETAIL_PAGE;
  const { getConfigByName } = useConfig();
  const JOBIQO_MATCHING = !!getConfigByName('jobiqo_matching')?.value[0];
  const isJobPublished = getIsJobPublished(job);

  useEffect(() => {
    // Set the job id so we can create a similar job alert.
    setJobId(job.id);
  }, [job, setJobId]);

  return (
    <Layout fullWidthContainer={true}>
      {job.organizationProfile && (
        <OrganizationBannerWidget organization={job.organizationProfile} />
      )}
      <Main size="medium">
        <Box
          paddingClean={true}
          {...(job.organizationProfile && {
            marginTop: ['-30px'],
            style: { zIndex: 2, position: 'relative' },
          })}
        >
          <JobDetailPanel
            job={job}
            user={user}
            noFollowExternalLinks={noFollowExternalLinks}
            plainTextExternalLinks={plainTextExternalLinks}
          />
        </Box>
        <Spacing size="large" />
        {isJobPublished && (
          <>
            {!!job.organizationProfile?.gallery?.length && (
              <>
                <OrganizationGalleryWidget
                  organization={job.organizationProfile}
                />
                <Spacing size="large" />
              </>
            )}
            {!!job.organizationProfile?.videos?.length && (
              <>
                <OrganizationVideoGalleryWidget
                  organization={job.organizationProfile}
                />
                <Spacing size="large" />
              </>
            )}
            {SHOW_ORGANIZATION_JOBS_ON_DETAIL_PAGE &&
              !!job.organizationProfile && (
                <>
                  <JobsByOrganizationProfileId
                    organization={job.organizationProfile}
                    currentJobId={job.id}
                  />
                  <Spacing size="large" />
                </>
              )}
            {SHOW_ORGANIZATION_PRESENTATION_ON_DETAIL_PAGE &&
              !!job.organizationProfile && (
                <>
                  <OrganizationAboutWidget
                    organization={job.organizationProfile}
                    trimmed={true}
                  />
                  <Spacing size="large" />
                </>
              )}
          </>
        )}
        <JobAlertModal />
        {JOBIQO_MATCHING && isJobPublished && (
          <>
            {isRecruiter(user) ? (
              job.canEdit && (
                <ApplicantRecommenderApplicants2Job jobId={job.id} />
              )
            ) : (
              <JobRecommenderJobs2Job jobId={job.id} />
            )}
            <Spacing size="large" />
          </>
        )}
        {JOBIQO_MATCHING && !isJobPublished && (
          <ScrollToElement>
            <JobRecommenderSimilarJobs jobId={job.id} />
            <Spacing size="large" />
          </ScrollToElement>
        )}
      </Main>
    </Layout>
  );
};

export default JobPageDetail;
